<template>
    <WrGenicHero :badge-text="heroBadgeText" :heading-line-1="heroHeadingLine1" :heading-line-2="heroHeadingLine2"
        :description="heroDescription" :cta-text="heroCtaText" :demo-cta-text="heroDemoCtaText"
        :redirect-links="heroRedirectLinks" :hero-prompts="heroPrompts" :weather-card="heroWeatherCard"
        :order-card="heroOrderCard" :history-card="heroHistoryCard" @demo-clicked="openDemoModal" />

    <section class="relative z-10 overflow-visible grid place-items-center max-sm:mt-6 mt-0">
        <div
            class="absolute w-[364px] h-[383px] dark:block hidden max-sm:dark:hidden bg-[#4A20FF] blur-[150px] top-30% -left-56 opacity-80 -translate-x-1/2" />
        <div class="max-w-screen-xl mx-auto px-4 w-full">
            <WrGenicComparison heading="Eliminating the" subheading="Bottleneck with "
                intro="Legacy AI forces complex data into static plain text. GenicUI turns AI outputs into fast, brand-compliant UI components instantly."
                :problem-label="problemLabel" :problems="problems" :solution-label="solutionLabel"
                :solutions="solutions" cta-text="See it in Action" @cta-clicked="openModal" />
        </div>
    </section>

    <section class="relative z-10 overflow-visible max-sm:mt-16 mt-16">
        <WrAiGenicInterfaces :heading-line-1="aiInterfacesHeadingLine1" :heading-line-2="aiInterfacesHeadingLine2"
            :heading-accent="aiInterfacesHeadingAccent" :prompts="aiInterfacesPrompts"
            :prompt-label="aiInterfacesPromptLabel" :order-card="aiInterfacesOrderCard"
            :support-card="aiInterfacesSupportCard" :revenue-card="aiInterfacesRevenueCard" />
    </section>

    <section class="relative z-10 pb-10 lg:pb-24 grid place-items-center max-sm:mt-16 mt-10 overflow-visible">
        <div
            class="absolute w-[364px] h-[383px] dark:block hidden max-sm:dark:hidden bg-[#573EC2] blur-[150px] -top-5 -right-56 opacity-80 translate-x-1/2" />
        <div
            class="absolute w-[364px] h-[383px] dark:block hidden max-sm:dark:hidden bg-[#573EC2] blur-[150px] top-30% -left-56 opacity-80 -translate-x-1/2" />
        <img v-if="!isLight" src="/images/genui-working-dark.webp" alt=""
            class="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[1268px] h-auto z-0 pointer-events-none opacity-70"
            loading="lazy" />
        <img v-if="isLight" src="/images/genui-working-light.webp" alt=""
            class="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[1268px] h-auto z-0 pointer-events-none opacity-70"
            loading="lazy" />
        <div class="max-w-screen-xl mx-auto px-4 w-full relative z-10">
            <WrGenicUiWorking heading="How GenicUI Works" subheading="Under The Hood"
                description="A clean, 3-step workflow that shows how simple integration is" :steps="genicUiSteps"
                cta-text="Get Started in Minutes" @cta-clicked="openModal" />
        </div>
    </section>

    <section class="relative z-10 pb-10 lg:pb-24 overflow-hidden grid place-items-center">
        <div class="max-w-screen-xl mx-auto px-4 w-full">
            <WrGenicUiCapabilities heading="Explore the Powerful" heading-suffix="Capabilities of GenicUI"
                intro="Render live AI updates using your existing components without slowing down your app or breaking your design."
                :stream-label="capabilityStreamLabel" :stream-badge="capabilityStreamBadge"
                :capability-cards="capabilityCards" :card1-title="capabilityCard1Title"
                :card1-description="capabilityCard1Description" :existing-component-label="existingComponentLabel"
                :product="capabilityProduct" :add-bag-text="addBagText" :native-theme-note="nativeThemeNote"
                :card2-title="capabilityCard2Title" :card2-description="capabilityCard2Description"
                :ai-agents="aiAgents" :models-label="modelsLabel" :schema-engine-label="schemaEngineLabel"
                :identical-result="identicalResult" :card3-title="capabilityCard3Title"
                :card3-description="capabilityCard3Description" cta-text="Connect Your First Component"
                @cta-clicked="openModal" />
        </div>
    </section>

    <!-- <section class="relative z-10 pb-10 lg:pb-36 overflow-visible grid place-items-center max-sm:mt-6 mt-10">
        <div
            class="absolute w-[364px] h-[383px] dark:block hidden max-sm:dark:hidden bg-[#573EC2] blur-[150px] -bottom-2 -right-56 opacity-80 translate-x-1/2" />
        <div
            class="absolute w-[364px] h-[383px] dark:block hidden max-sm:dark:hidden bg-[#573EC2] blur-[150px] -bottom-5 -left-56 opacity-80 -translate-x-1/2" />
        <div class="max-w-screen-xl mx-auto px-4 w-full">
            <WrGenicUiVideoPlayer :video-url="demoVideoUrl" :heading-line-1="videoHeadingLine1"
                :heading-line-2="videoHeadingLine2" :subheading="videoSubheading"
                :overlay-text="videoOverlayText" :cta-text="videoCtaText" @cta-clicked="openModal" />
        </div>
    </section> -->

    <section class="relative z-10 pb-20 lg:pb-36 overflow-hidden max-sm:mt-6 mt-1">
        <WrGenicApplications :heading="applicationsHeading" :heading-suffix="applicationsHeadingSuffix"
            :description="applicationsDescription" :cta-text="applicationsCtaText" :tabs="applicationTabs"
            @cta-clicked="openModal" />
    </section>


    <WrGenicWhyChoose :heading="whyChooseHeading" :subtitle="whyChooseSubtitle" :cta-text="whyChooseCtaText"
        :pills="whyChoosePills" @cta-clicked="openModal" />

    <section
        class="mb-20 max-sm:mb-0 dark:[--bg-accordion:linear-gradient(93.26deg,#161D54_33.61%,#201347_96.74%)] dark:[--border-accordion:linear-gradient(93.26deg,#161D54_33.61%,#201347_96.74%)] max-sm:mt-14">
        <WrDesignFaqsSection :heading="faq.heading" :suffix="faq.suffix" :subtext="faq.subtext"
            :filter-tags="faq.filterTags" :faq-items="faqItems"
            active-tag-class="text-[#4753DC] dark:text-[#579DFF] font-medium"
            cta-append-class="bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]"
            arc-gradient="bg-[linear-gradient(0deg,#A3A9ED_31.11%,#A3A9ED00_51.33%,#A3A9ED00_57.98%,#A3A9ED_62.05%)] dark:bg-[linear-gradient(0deg,#1F0134_31.11%,#4D27B600_51.33%,#855BF200_57.98%,#22206D_62.05%)]"
            @cta-clicked="openModal" />
    </section>

    <section
        class="relative py-10 pt-0 max-sm:pt-0 lg:pb-40 overflow-visible grid place-items-center max-sm:mt-12 max-sm:pb-0">
        <div
            class="absolute -translate-x-1/2 -top-[480px] -left-[10%] size-[764px] rounded-full dark:bg-[#130230] dark:bg-[linear-gradient(136.28deg,rgba(51,4,79,0)_30.43%,#260049_54.53%,#36006F_92.37%)] shadow-[39px_5px_33.1px_15px_#130230] opacity-20 dark:block hidden max-sm:dark:hidden">

        </div>
        <div
            class="absolute translate-x-1/2 -bottom-[20px] right-3 size-[189px] rounded-full dark:bg-[linear-gradient(157.91deg,rgba(51,4,79,0)_9.32%,#36006F_86.78%)] opacity-30 dark:block hidden max-sm:dark:hidden">
        </div>
        <div class="container mx-auto px-4 relative z-10">
            <div class="text-center">
                <WrOfficialPartners
                    :append-class="'px-4 py-5 md:px-8 md:py-6 dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#060B28_79.8%)]'"
                    :title="officialPartnersTitle" :partners="officialPartners"
                    :partnersAppendClass="'justify-center items-center'" />
            </div>
        </div>
    </section>

    <section class="relative z-10 pb-20 lg:pb-36 overflow-hidden grid place-items-center max-sm:mt-12 mt-20">
        <div class="max-w-screen-xl mx-auto px-4 w-full">
            <WrWorkforceGrowth :subheading="workforceSubheading" :description="workforceDescription"
                :achievements="workforceAchievements"
                append-class="bg-[linear-gradient(118.11deg,#FFFFFF_4.47%,#F3F0FF_79.8%)] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#060B28_79.8%)]"
                container-class="bg-[linear-gradient(135.39deg,#BDB1FF_7.01%,#6952ED_93.21%)] dark:bg-[linear-gradient(135.39deg,#3840B5_7.01%,#12123D_93.21%)]" />
        </div>
    </section>

    <section class="relative dark:bg-none overflow-hidden z-20">
        <div class="container-wrapper-xl">
            <h2 class="text-center">
                <WrGradientText text="Trusted for excellence and efficiency" append-class="text-3xl lg:text-[48px]" />
            </h2>
            <p class="leading-[1.8] text-lg text-[var(--foreground)] font-anvtgrade-book mb-11">
                WebReinvent is trusted by leading organisations to enhance their
                development and operational efficiency.
            </p>
            <!-- Carousel Section -->
            <WrTestimonialCardCarousel class="mb-12" :testimonials="testimonials" />
            <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
                append-class="bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]">View All</WrButton>
        </div>
    </section>


    <WrContactFormPopup v-if="visible" :isOpen="visible" @close="visible = false" class="z-30" />
    <WrGenicDemoModal v-if="demoVisible" :isOpen="demoVisible" @close="demoVisible = false" />
</template>

<script setup>
import WrGenicHero from "~/components/molecules/WrGenicHero.vue";
import WrAiGenicInterfaces from "~/components/molecules/WrAiGenicInterfaces.vue";
import WrGenicApplications from "~/components/molecules/WrGenicApplications.vue";
import WrWorkforceGrowth from "~/components/molecules/WrWorkforceGrowth.vue";
import WrGenicComparison from "~/components/molecules/WrGenicComparison.vue";
import WrGenicUiWorking from "~/components/molecules/WrGenicUiWorking.vue";
import WrGenicUiCapabilities from "~/components/molecules/WrGenicUiCapabilities.vue";
import WrGenicUiVideoPlayer from "~/components/molecules/WrGenicUiVideoPlayer.vue";
import WrRobot from "~/components/molecules/WrRobot.vue";
import WrAddItem from "~/components/molecules/WrAddItem.vue";
import WrUiText from "~/components/molecules/WrUiText.vue";
import WrGithub from "~/components/molecules/WrGithub.vue";
import WrArrowUpRight from "~/components/molecules/WrArrowUpRight.vue";
import WrGenicEnterpriseIcon from "~/components/molecules/WrGenicEnterpriseIcon.vue";
import WrGenicFintechIcon from "~/components/molecules/WrGenicFintechIcon.vue";
import WrGenicSupplyIcon from "~/components/molecules/WrGenicSupplyIcon.vue";

const demoVideoUrl = "/videos/genic-ui-demo.mp4";

definePageMeta({
    layout: "genic-ui-layout",
});

const { isLight } = useThemeMode();
const { isMobile } = useDevice();

const visible = ref(false);

const openModal = () => {
    visible.value = true;
};

const demoVisible = ref(false);

const openDemoModal = () => {
    demoVisible.value = true;
};

// ── Hero ─────────────────────────────────────────────────────────────
const heroBadgeText = "FLAGSHIP PRODUCT BY WEBREINVENT AI SOFTWARE FACTORY";
const heroHeadingLine1 = "Stop Reading Answers";
const heroHeadingLine2 = "Start using them";
const heroDescription =
    "GenicUI Developed by Webreinvent to turn flat AI responses into live, click-ready tools and dynamic web components instantly.";
const heroCtaText = "Explore GenicUI";
const heroDemoCtaText = "Watch Demo";
const heroRedirectLinks = [
    {
        icon: WrGithub,
        text: "Open Source",
        link: "https://github.com/vaah-ai/GenicUI",
    },
    {
        icon: WrArrowUpRight,
        text: "Documentation",
        link: "https://genicui.vaah.ai/getting-started/introduction",
    },
];
const heroPrompts = [
    {
        text: "What's the weather in San Francisco",
        dot: "#34d399",
        left: "5%",
        top: "34%",
        delay: "-0.5s",
        tint: "green",
    },
    {
        text: "Show my recent order status",
        dot: "#60A5FA",
        left: "9%",
        top: "50%",
        delay: "-2.3s",
        tint: "blue",
    },
    {
        text: "Show my recent complaint status",
        dot: "#a78bfa",
        left: "4%",
        top: "66%",
        delay: "-4.1s",
        tint: "purple",
    },
];
const heroWeatherCard = {
    icon: "streamline:interface-weather-sun-photos-light-camera-mode-brightness-sun-photo-full",
    city: "San Francisco",
    condition: "Sunny · Wind 6 mph",
    temp: "64°",
};
const heroOrderCard = {
    title: "Order #23147",
    steps: ["Processing", "Shipped", "Delivered"],
    etaIcon: "streamline:shipping-truck",
    etaText: "Arriving Tomorrow",
};
const heroHistoryCard = {
    title: "Order History",
    badge: "Open",
    items: [
        {
            id: "#4820",
            status: "Closed",
            note: "Refund not initiated.",
            created: "Created 14 days ago",
        },
        {
            id: "#4819",
            status: "Closed",
            note: "Order not received.",
            created: "Created 2 months ago",
        },
    ],
};

// ── AI Interfaces diagram ────────────────────────────────────────────
const aiInterfacesHeadingLine1 = "Experience the Future of";
const aiInterfacesHeadingLine2 = "AI Interfaces with";
const aiInterfacesHeadingAccent = "GenicUI";
const aiInterfacesPrompts = [
    { text: "Show my recent order status", dot: "#34D399" },
    { text: "Show my recent complaint status", dot: "#60A5FA" },
    { text: "Show my revenue of 7 days", dot: "#A78BFA" },
];
const aiInterfacesPromptLabel = "Prompt";
const aiInterfacesOrderCard = {
    title: "Order #23147",
    steps: ["Processing", "Shipped", "Delivered"],
    etaIcon: "streamline:shipping-truck",
    etaText: "Arriving Tomorrow",
    mapFrom: "San Francisco",
    mapTo: "New York",
    statusIcon: "streamline:shipping-box-1",
    statusTitle: "Shipped",
    statusSub: "On track to arrive tomorrow",
};
const aiInterfacesSupportCard = {
    id: "#4821",
    status: "Open",
    title: "Payment Issue",
    created: "Created 2h ago",
    ownerIcon: "streamline:user-single-neutral-male",
    owner: "Customer Support",
    historyTitle: "Resolution History",
    history: [
        {
            id: "#4820",
            status: "Closed",
            note: "Refund not initiated.",
            created: "Created 14 days ago",
        },
        {
            id: "#4819",
            status: "Closed",
            note: "Order not received.",
            created: "Created 2 months ago",
        },
    ],
};
const aiInterfacesRevenueCard = {
    title: "Revenue",
    range: "Last 7 days",
    bars: [55, 70, 50, 62, 85, 72, 100, 58, 45],
    accentIndex: 7,
    total: "$12,430",
    delta: "12%",
    metricsTitle: "Key Metrics",
    metrics: [
        {
            icon: "streamline:shopping-cart-1",
            label: "Orders",
            value: "152",
            delta: "↑ 8%",
            up: true,
        },
        {
            icon: "streamline:user-multiple-group",
            label: "New Customers",
            value: "38",
            delta: "↑ 15%",
            up: true,
        },
        {
            icon: "streamline:interface-edit-view-eye-eyeball-open-view",
            label: "Page Views",
            value: "4,892",
            delta: "↑ 6%",
            up: true,
        },
    ],
};

// ── Video player ─────────────────────────────────────────────────────
const videoHeadingLine1 = "See The Real Magic";
const videoHeadingLine2 = "of GenicUI";
const videoSubheading =
    "Toggle the demo to see how GenicUI renders live, interactive components directly from raw AI streams.";
const videoOverlayText = "A simpler way to bring your UI components to AI.";
const videoCtaText = "Lets Get Started";

// ── Real-World Applications ──────────────────────────────────────────
const applicationsHeading = "Real-World Applications";
const applicationsHeadingSuffix = "Across Industries";
const applicationsDescription =
    "Empower every sector with real-time, interactive components that seamlessly connect AI to your design system.";
const applicationsCtaText = "Experience GenicUI";
const applicationTabs = [
    {
        title: "Enterprise SaaS & B2B Software",
        text: "Native software panels, grant, user management, and onboarding flows.",
        icon: WrGenicEnterpriseIcon,
        demo: {
            userMessage: "Add 2 team leads to the Admin role.",
            agentMessage: "Role matrix prepared. 2 users updated for approval.",
            chip: "#alpha-team-workshop",
            panelTitle: "Workspace Access Control",
            panelPill: "2 USERS TARGETED",
            panel: {
                type: "table",
                head: ["Member Profile", "Assigned Role", "Status"],
                rows: [
                    {
                        name: "Sarah Chen",
                        role: "Lead Architect",
                        tag: "Admin",
                        initials: "SC",
                        color: "linear-gradient(135deg, #4b4fd6, #262b7e)",
                        status: "stable",
                        statusLabel: "Stable",
                    },
                    {
                        name: "Alex Rivera",
                        role: "Principal Eng",
                        tag: "Admin",
                        initials: "AR",
                        color: "linear-gradient(135deg, #8b5cf6, #5b21b6)",
                        status: "audit",
                        statusLabel: "Audit",
                    },
                ],
            },
            modulesLabel: "Recommended Permission Modules",
            modules: [
                { name: "Billing Access", hint: "Access granted", on: true },
                { name: "API Keys", hint: "Write access", on: true },
            ],
            applyText: "All 2 users confirmed pending attestation.",
            applyLabel: "Apply Changes",
        },
    },
    {
        title: "Fintech & Financial Services",
        text: "Live portfolio views, loan portals, risk dashboards, and compliance forms.",
        icon: WrGenicFintechIcon,
        demo: {
            userMessage: "Show my portfolio performance for Q3.",
            agentMessage: "Here's your Q3 portfolio performance.",
            chip: "#q3-portfolio",
            panelTitle: "Portfolio Performance",
            panelPill: "Q3 2026",
            panel: {
                type: "chart",
                axis: ["Jul", "Aug", "Sep"],
                holdings: [
                    { name: "Apple Inc.", ticker: "AAPL", allocation: "24%" },
                    { name: "Microsoft", ticker: "MSFT", allocation: "18%" },
                ],
            },
        },
    },
    {
        title: "Supply Chain & Logistics",
        text: "Real-time fleet tracking, smart inventory dashboards, and allocation portals.",
        icon: WrGenicSupplyIcon,
        demo: {
            userMessage: "Reallocate 120 units from Hub West to Hub East.",
            agentMessage: "Rebalance planned. Route is live and tracking in real time.",
            chip: "#hub-east-rebalance",
            panelTitle: "Rebalance Route",
            panelPill: "ETA 6:40 PM",
            panel: {
                type: "route",
                stops: [
                    { name: "Hub West", time: "9:10 AM", state: "done" },
                    { name: "Cross-dock", time: "1:35 PM", state: "active" },
                    { name: "Hub East", time: "6:40 PM", state: "upcoming" },
                ],
                meta: "120 units · 2 vehicles · Lane 4",
            },
            modulesLabel: "Dispatch Checks",
            modules: [
                { name: "Cold-chain", hint: "Active", on: true },
                { name: "Last-mile", hint: "Scheduled", on: true },
            ],
            applyText: "120 units staged for dispatch.",
            applyLabel: "Dispatch",
        },
    },
];

// ── Why Choose ───────────────────────────────────────────────────────
const whyChooseHeading = "Why Choose GenicUI?";
const whyChooseSubtitle =
    'Show real <strong>frontend components</strong> directly in <strong>AI chats</strong> no extra code, instant live updates, and complete security.';
const whyChooseCtaText = "Get Started Instantly";
const whyChoosePills = [
    { label: "100% IP & Data Ownership", x: "42.4%", y: "4.6%", w: "125px" },
    { label: "Instant UI Updates", x: "63%", y: "12.1%", w: "118px" },
    { label: "Native Component Bridge", x: "19.9%", y: "17.5%", w: "158px" },
    { label: "Shadow DOM Isolation", x: "40.6%", y: "21.3%", w: "130px" },
    { label: "Open Source Leadership", x: "60.1%", y: "25%", w: "116px" },
    { label: "Framework Agnostic", x: "81.5%", y: "19.5%", w: "140px" },
    { label: "Zero UI Rewrites", x: "24.3%", y: "32.5%", w: "148px" },
    { label: "Enterprise Security First", x: "76.9%", y: "31.2%", w: "172px" },
    { label: "MCP Protocol Native", x: "9.5%", y: "40.7%", w: "142px" },
    { label: "Standardized Protocol", x: "92.5%", y: "38.2%", w: "126px" },
    { label: "Zero Custom Adapters", x: "71.2%", y: "42.2%", w: "154px" },
];

// ── Official Partners ────────────────────────────────────────────────
const officialPartnersTitle = "Official Partner Of";
const officialPartners = [
    {
        name: "Laravel",
        width: isMobile ? "38" : "47",
        height: isMobile ? "40" : "49",
        img: "/images/icons/Laravel.webp",
        darkImg: "/images/laravelDarkimage.webp",
        alt: "Laravel Icon",
        link: "https://laravel.com/partners?region=asia",
    },
    {
        name: "Nuxt",
        width: isMobile ? "37" : "59",
        height: isMobile ? "23" : "39",
        img: "/images/icons/nuxt.webp",
        darkImg: "/images/nuxtDarkImage.webp",
        alt: "Nuxt icon",
        link: "https://nuxt.com/enterprise/agencies/webreinvent",
    },
    {
        name: "Flutter",
        width: isMobile ? "23" : "45",
        height: isMobile ? "31" : "45",
        img: "/images/icons/flutter.webp",
        darkImg: "/images/icons/flutter-dark.webp",
        alt: "Flutter Icon",
        link: "https://flutter.dev/consultants?locations=Asia/",
    },
    {
        name: "Vue",
        width: isMobile ? "37" : "49",
        height: isMobile ? "30" : "42",
        img: "/images/icons/Vue.webp",
        darkImg: "/images/vueDarkImage.webp",
        alt: "Vue icon",
        link: "https://vuejs.org/partners/webreinvent.html",
    },
    {
        name: "Builder.io",
        width: isMobile ? "22" : "32",
        height: isMobile ? "26" : "36",
        img: "/images/icons/Builder.webp",
        darkImg: "/images/BuilderDarkImage.webp",
        alt: "Builder.io icon",
        link: "https://www.builder.io/m/partners/webreinvent",
    },
    {
        name: "Phrase",
        width: isMobile ? "23" : "33",
        height: isMobile ? "38" : "48",
        img: "/images/icons/Phrase.webp",
        darkImg: "/images/icons/phrase-dark.webp",
        alt: "Phrase IO Alt Icon",
        link: "https://phrase.com/partners/",
    },
];

// ── Workforce Growth ─────────────────────────────────────────────────
const workforceSubheading = 'Your Partner in <br class="max-md:hidden" /> Workforce Growth';
const workforceDescription = "Optimize Costs and Streamline Workforce";
const workforceAchievements = [
    { count: "100+", title: "Trained IT<br> Professional" },
    { count: "20+", title: "Experienced AI<br> Engineers" },
    { count: "60+", title: "Frontend, UI/UX, DevOps, Testers" },
    { count: "2.5%", title: "Lesser Defect<br>Rate than" },
];

// ── Testimonials ─────────────────────────────────────────────────────
const testimonials = [
    {
        description:
            "We chose WebReinvent after much research. They gave value to my business and were sincere throughout the project lifecycle.",
        authName: "Zack Rinkins",
        authRole: "CEO",
        authImg: "/images/zack.webp",
    },
    {
        description:
            "From day one, the team was friendly and helpful. They not only build apps but also train you well to use them effectively.",
        authName: "Tiffani A. Gilbert",
        authRole: "CEO",
        authImg: "/images/taffain.webp",
    },
    {
        description:
            "Thanks for fast and efficient delivery. We're impressed with WebReinvent's service and will definitely recommend them.",
        authName: "Ankur Shah",
        authRole: "CEO",
        authImg: "/images/ankur.webp",
    },
    {
        description:
            "They appeared as the ultimate solution, bringing brilliant ideas in a tight deadline and making everything work smoothly.",
        authName: "Pranjal",
        authRole: "CEO",
        authImg: "/images/pranjal.webp",
    },
    {
        description:
            "WebReinvent is a highly skilled team with an eye for detail. Project management and communication worked really well.",
        authName: "Jeff Crystal",
        authRole: "CEO",
        authImg: "/images/jeffCrystal.webp",
    },
    {
        description:
            "WebReinvent is a skilled team with attention to detail. We got exactly what we wanted and more through great project tools.",
        authName: "Daniel Bates",
        authRole: "CEO",
        authImg: "/images/danielBates.webp",
    },
    {
        description:
            "Thank you for a great job on our website. The brilliant design you created gave our company a professional new look.",
        authName: "Pawan Shah",
        authRole: "CEO",
        authImg: "/images/pawanShah.webp",
    },
    {
        description:
            "I've worked with WebReinvent for over a year. They are timely, professional, and creative in enhancing my site ideas.",
        authName: "Darrell Bennett",
        authRole: "CEO",
        authImg: "/images/darrellBennett.webp",
    },
    {
        description:
            "Working with WebReinvent was pleasant. They responded quickly and clearly understood the project scope, delivering well.",
        authName: "Geoffrey Nwachukwu",
        authRole: "CEO",
        authImg: "/images/geoffreyNwachukwu.webp",
    },
];

const problemLabel = "Where AI Gets Stuck";
const problems = [
    "Plain-text & raw JSON",
    "No control over design",
    "Slow, laggy experience",
    "Extra backend code needed",
    "Off-brand, broken visuals",
    "High maintenance effort",
];
const solutionLabel = "How GenicUI Unlocks It";
const solutions = [
    "Your own native UI",
    "Direct, simple connection",
    "Fully interactive chat",
    "Zero extra middleman code",
    "Instant, lightweight updates",
    "Safe, isolated rendering",
];

const genicUiSteps = [
    {
        label: "Step 1",
        title: "Wrap Your Component Library",
        description:
            "Define each component's props, states, and events once in the registry the MCP server does the rest, exposing them to agents as a validated, searchable tool surface.",
    },
    {
        label: "Step 2",
        title: "Declare Component Contracts",
        description:
            "Connect any AI Agents to the GenicUI server with just one line of setup. From there, it can find and render your components. It works with any AI Agent, no lock-in.",
    },
    {
        label: "Step 3",
        title: "Let Agents Drive the UI",
        description:
            "Real, clickable components appear right in your app and every user action is felt by the AI immediately, so the UI keeps up with the conversation.",
    },
];

const capabilityStreamLabel = "Live MCP Stream";
const capabilityStreamBadge = "Zero Middleware";
const capabilityCards = [
    {
        icon: WrRobot,
        circleClass: 'bg-[#4F46E5] dark:bg-[#4F46E533] border border-[#6366F166]',
        text: 'Emits Intent',
        heading: 'AI Agent',
        headingClass: 'text-[#1E1B4B] dark:text-[#FFFFFF]',
        appendClass: 'bg-[#F5F7FF] dark:bg-[#07134B] border border-[#6366F130]',
        textClass: 'text-[#5A5478] dark:text-[#94A3B8]'
    },
    {
        icon: WrAddItem,
        circleClass: 'bg-[#06B6D4] dark:bg-[#06B6D433] border border-[#22D3EE80]',
        text: 'SSE Protocol',
        heading: 'MCP Bridge',
        headingClass: 'text-[#0E7490] dark:text-[#A5F3FC]',
        appendClass: 'bg-[#F5FDFF] dark:bg-[#08334466] border border-[#22CCEE66]',
        textClass: 'text-[#0E7490] dark:text-[#67E8F9]'
    },
    {
        icon: WrUiText,
        circleClass: 'bg-[#022C22] border border-[#10B98166]',
        text: '12ms Render',
        heading: 'Native UI',
        headingClass: 'text-[#1E1B4B] dark:text-[#FFFFFF]',
        appendClass: 'bg-[#F5FDF8] dark:bg-[#022C224D] border border-[#04785766]',
        textClass: 'text-[#047857] dark:text-[#34D399]'
    }
];
const capabilityCard1Title = "Built-In MCP Standard";
const capabilityCard1Description =
    "Connect AI to your UI and sync updates instantly using your own component library.";

const existingComponentLabel = "EXISTING COMPONENT";
const capabilityProduct = {
    name: "Aurora Pro Headset",
    description: "Active Noise Cancellation • Space Grey",
    price: "$249.00",
    status: "In Stock",
};
const addBagText = "Add to Bag";
const nativeThemeNote = "100% Native Theme • Zero Custom CSS Required";
const capabilityCard2Title = "Zero Frontend Rewrites";
const capabilityCard2Description =
    "Works with React, Vue, or Svelte. GenicUI connects AI directly to your existing design system, no rebuilds needed.";

const aiAgents = [
    {
        text: 'Claude 3.5',
        textClass: 'text-[#B45309] dark:text-[#FCD34D]',
        appendClass: 'bg-[#FEF3C7] dark:bg-[#F59E0B33] border border-[#F59E0B66]',
        contentClass: 'bg-[#FBBF24]'
    },
    {
        text: 'GPT-4o',
        textClass: 'text-[#047857] dark:text-[#6EE7B7]',
        appendClass: 'bg-[#D1FAE5] dark:bg-[#022C2280] border border-[#04785766]',
        contentClass: 'bg-[#34D399]'
    },
    {
        text: 'Gemini 1.5',
        textClass: 'text-[#1D4ED8] dark:text-[#93C5FD]',
        appendClass: 'bg-[#DBEAFE] dark:bg-[#17255480] border border-[#1E40AF99]',
        contentClass: 'bg-[#22D3EE]'
    }
];
const modelsLabel = "3 Models";
const schemaEngineLabel = "1 Schema Engine";
const identicalResult = {
    heading: "Identical UI Result",
    description: "Strict Type-Safe Component Tree",
};
const capabilityCard3Title = "Any LLM Compatibility";
const capabilityCard3Description =
    "Works with React, Works with any AI model, Claude, Codex etc. Get the exact same reliable UI every time.";

const faq = {
    heading: "Got Questions?",
    suffix: " We have answers.",
    subtext:
        "Everything you need to know about GenicUI's runtime, protocol, and integration.",
    filterTags: ["All", "Core", "Benefits", "Integration", "Enterprise"],
};

const faqItems = [
    {
        title: "What exact problem does GenicUI solve for web applications?",
        description:
            "Today, AI agents can only reply with text, and that breaks the moment a user needs a clickable table, a fillable form, or a live dashboard. GenicUI lets the agent render real, interactive components in your app and hear what the user does with them, closing the gap between conversation and action.",
        tags: ["Core"],
    },
    {
        title: "How does GenicUI work under the hood?",
        description:
            "GenicUI runs an MCP server that exposes your UI components, declared as schemas in a registry, to any AI agent as callable tools. The agent renders live components in your conversation, and every user click or input streams back to the agent as an event, so it can react in real time.",
        tags: ["Core"],
    },
    {
        title: "How does GenicUI impact user conversion rates?",
        description:
            "Users get <strong>interactive, click-ready components</strong> — tables, forms, pickers — instead of a wall of text. They can filter, select, and submit right inside the conversation, which shortens the path from intent to action.",
        tags: ["Benefits"],
    },
    {
        title: "Can GenicUI help out operational and customer support costs?",
        description:
            "Yes. Agents render real UI to collect structured input, so more tasks are <strong>resolved inside the conversation without a human handoff</strong>. One component catalog is reused across every surface, and the schema-validated protocol means fewer flaky integrations.",
        tags: ["Benefits"],
    },
    {
        title: "Is GenicUI suitable for enterprise applications or strictly B2C?",
        description:
            "Both. GenicUI is an <strong>infrastructure layer</strong>, not a consumer product — B2C apps can embed agent-driven UI, while enterprise teams can run it multi-tenant with per-tenant API keys and allow-lists, or fully on-premise.",
        tags: ["Enterprise"],
    },
    {
        title:
            "How does GenicUI maintain our brand's UI design and accessibility standards?",
        description:
            "GenicUI never brings its own UI. It renders your own components through a registry that wraps your existing design system, so everything the agent shows inherits your brand's styling and accessibility attributes by default.",
        tags: ["Core"],
    },
    {
        title: "Which frontend frameworks and AI backends does GenicUI support?",
        description:
            "Framework-agnostic: components are Web Components with adapters for <strong>Vue/Nuxt, React, SvelteKit, and vanilla HTML</strong>. The server runs on Bun 1.3.12+ or Node.js 20 LTS+, and any MCP-capable agent — Claude, GPT, Cursor, or your own — works out of the box.",
        tags: ["Integration"],
    },
    {
        title:
            "How does GenicUI handle sensitive user data (e.g., credit cards or passwords)?",
        description:
            "GenicUI is a <strong>render and transport layer, not a data store</strong> — it never takes custody of user data. Sensitive inputs flow through your own components and backend, and the agent only receives the events you explicitly subscribe it to.",
        tags: ["Enterprise"],
    },
    {
        title:
            "How long does it take to integrate GenicUI into our existing stack?",
        description:
            "Integration takes hours, not weeks. One command starts the server, you list your components once, and your AI tool connects with a single line, leaving your existing stack untouched.",
        tags: ["Integration"],
    },
    {
        title:
            "Can GenicUI be used in offline or self-hosted enterprise environments?",
        description:
            "Yes. The server is fully self-hosted with a single <strong>bun run start</strong> command, storage in memory, SQLite, or Postgres, and no external SaaS dependency. Point the agent bridge at any LLM endpoint, including a local model — it runs on your private network.",
        tags: ["Enterprise"],
    },
];

useHead({
    title: "GenicUI | Turn AI Outputs into Live, Interactive UI Components",
    meta: [
        {
            property: "og:title",
            content: "Software Products, Web and Mobile App Development Company",
        },
        {
            name: "twitter:title",
            content: "WebReinvent: Leading Web, Mobile & AI Product Company",
        },
        {
            name: "description",
            content:
                "GenicUI turns AI outputs into fast, brand-compliant, interactive UI components rendered live in your app via MCP — zero middleware, no rewrites.",
        },
        {
            name: "keywords",
            content:
                "GenicUI, AI UI components, MCP protocol, AI agent interface, live component rendering, WebReinvent",
        },
    ],
    script: [
        {
            type: "application/ld+json",
            innerHTML: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                name: "GenicUI",
                description:
                    "GenicUI turns AI outputs into fast, brand-compliant, interactive UI components rendered live in your app via MCP — zero middleware, no rewrites.",
                applicationCategory: "DeveloperApplication",
                operatingSystem: "Web",
                url: "https://webreinvent.com/products/genic-ui",
                sameAs: ["https://github.com/vaah-ai/GenicUI", "https://genicui.vaah.ai"],
                offers: {
                    "@type": "Offer",
                    price: "0",
                    priceCurrency: "USD",
                },
            }),
        },
        {
            type: "application/ld+json",
            innerHTML: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqItems.map((item) => ({
                    "@type": "Question",
                    name: item.title,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: item.description.replace(/<[^>]*>/g, ""),
                    },
                })),
            }),
        },
        {
            type: "application/ld+json",
            innerHTML: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: "https://webreinvent.com/",
                    },
                    {
                        "@type": "ListItem",
                        position: 2,
                        name: "Products",
                        item: "https://webreinvent.com/products",
                    },
                    {
                        "@type": "ListItem",
                        position: 3,
                        name: "GenicUI",
                        item: "https://webreinvent.com/products/genic-ui",
                    },
                ],
            }),
        },
    ],
});
</script>
